import { useEffect, useState } from "react";
import { Button } from "../../@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { MdModeEdit } from "react-icons/md";
import ShowStarRating from "./ShowStarRating";
import {
  useAddVendorReplyMutation,
  useGetRatingByIdQuery,
} from "../../api/api";
import SkeletonReviewDialog from "../../skeletonLoading/SkeletonReviewDialog";
import { useToast } from "../../@/components/ui/use-toast";
import { formatDate, truncateString, isMobileScreen } from "../../lib/utils";
import { Eye, X } from "lucide-react";
import MediaSwiper from "../../components/Swipers/MediaSwiper";
import SortupDialog from "../../components/common/SortupDialog";

type ReviewDialogProps = {
  reviewId: number;
  userRole: string;
  isOpen: boolean;
  onClose: () => void;
  isReadOnly: boolean;
};

export function ReviewDialog({
  reviewId,
  userRole,
  isOpen,
  onClose,
  isReadOnly,
}: ReviewDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(isOpen);
  const [reply, setReply] = useState("");

  const toast = useToast();

  const { data, isLoading, isSuccess } = useGetRatingByIdQuery(
    { productRatingId: reviewId },
    { skip: !isDialogOpen }
  );

  const [AddVendorReply, { isLoading: isReplyLoading }] =
    useAddVendorReplyMutation();

  useEffect(() => {
    if (isSuccess && data) {
      setReply(data?.data?.vendorReply || "");
    }
  }, [isSuccess, data]);

  useEffect(() => {
    setIsDialogOpen(isOpen);
  }, [isOpen]);

  const handleReplyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReply(e.target.value);
  };

  const handleAddVendorReply = async () => {
    if (reply === "") return;
    const response = await AddVendorReply({
      addVendorReplyDto: {
        productRatingId: reviewId,
        vendorReply: reply,
      },
    });
    if (response?.data?.message === "Reply added successfully.") {
      setIsDialogOpen(false);
      setReply("");
      toast.toast({
        title: "Success!",
        description: "Reply added successfully.",
      });
      onClose();
    } else {
      toast.toast({
        title: "Error!",
        description: "Failed to add reply.",
      });
    }
  };

  const handleDialogOpen = (isEditMode: boolean) => {
    setIsDialogOpen(true);
  };

  return (
    <SortupDialog
      isOpen={isDialogOpen}
      onClose={() => {
        setIsDialogOpen(false);
        onClose();
      }}
      header={isReadOnly ? "Review" : "Reply Review"}
    >
      <div className={`${isMobileScreen ? "w-full" : "w-[36vw]"}`}>
        {isLoading ? (
          <SkeletonReviewDialog />
        ) : (
          <>
            {/* User Review Section */}
            <div className="flex items-start space-x-4 mt-4">
              <img
                src={data?.data?.productImage || "/storeDummyImg.png"}
                alt="Product"
                className="w-16 h-16 rounded border border-gray-300"
              />
              <div className="overflow-hidden text-wrap w-full">
                <h2 className="text-lg font-semibold">
                  {data?.data?.productName}
                </h2>
                <p className="text-sm font-medium text-wrap">
                  {truncateString(data?.data?.productDescription || "", 150)}
                </p>
              </div>
            </div>
            <div className="bg-white border border-lightGrayBorderColor p-4 rounded-md mt-4">
              <div className="flex justify-between">
                <div className="flex items-center space-x-3">
                  <img
                    src="/userDummyImg.jpg"
                    alt="Reviewer"
                    className="w-12 h-12 rounded-full border object-contain"
                  />
                  <div>
                    <p className="font-semibold">{data?.data?.customerName}</p>
                    <p className="text-sm text-customGrayBase">
                      {formatDate(data?.data?.createdAt, "dd MMM yyyy")}
                    </p>
                  </div>
                </div>
                <ShowStarRating rating={data?.data?.rating || 0} />
              </div>
              <p className="mt-3 text-sm text-customGrayBase">
                {data?.data?.review}
              </p>
            </div>

            {/* Media Section */}
            {data?.data?.multimedia && data.data.multimedia.length > 0 ? (
              <MediaSwiper
                mediaList={data.data.multimedia.map((media) => ({
                  ...media,
                  fileType: parseInt(media.fileType),
                }))}
              />
            ) : null}

            {/* Vendor Reply Section */}
            <div className="mt-4">
              {isReadOnly && data?.data?.vendorReply && (
                <div className="bg-white border p-4 rounded-md">
                  <div className="flex justify-between">
                    <div className="flex items-center space-x-3">
                      <img
                        src={data?.data?.vendorImage || "/storeDummyImg.png"}
                        alt="Vendor"
                        className="w-12 h-12 rounded-full border object-contain"
                      />
                      <div>
                        <p className="font-semibold">
                          {data?.data?.vendorName}
                        </p>
                        <p className="text-sm text-customGrayBase">
                          {formatDate(data?.data?.updatedAt, "dd MMM yyyy")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-customGrayBase">
                    {data?.data?.vendorReply}
                  </p>
                </div>
              )}

              {!isReadOnly && (
                <>
                  <label
                    htmlFor="reply"
                    className="block text-sm font-helveticaMedium text-customGrayDark"
                  >
                    {isReadOnly ? "Your reply" : "Write reply"}
                  </label>
                  <textarea
                    id="reply"
                    rows={4}
                    className="block p-2 w-full mt-1 border rounded-md shadow-sm focus:outline-customGreenColor sm:text-sm resize-none"
                    placeholder={isReadOnly ? "" : "Write your reply here..."}
                    value={reply}
                    onChange={handleReplyChange}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                  />
                </>
              )}
            </div>

            <DialogFooter>
              {!isReadOnly && (
                <div className="flex justify-start w-full">
                  <Button
                    disabled={reply === "" || isReplyLoading}
                    onClick={handleAddVendorReply}
                    type="submit"
                    className="bg-customGreenColor text-white mt-4 hover:bg-customGreenDisabled focus:border-none w-36 h-10"
                  >
                    {isReplyLoading ? "Replying..." : "Reply"}
                  </Button>
                </div>
              )}
            </DialogFooter>
          </>
        )}
      </div>
    </SortupDialog>
  );
}
